// ─── Official reports to the SEC · reading (sprint 21) ──────────────────────
//
// Shared rows, written by netlify/functions/sec-8k.js (sec_filings) and by
// the SEC jobs (companies, company_business, insider_transactions). Readable
// by any signed-in user (RLS); nothing here is the user's own.
import { supabase } from "../lib/supabase";
import { openMarketTrades, TRADES_DAYS } from "./insiderTrades";

const missing = e => /42P01|PGRST205/.test(String(e?.code));
const warn = (what, e) => { if (!missing(e)) console.warn(`[sec ${what}]`, e?.code, e?.message); };

// The companies rows of these symbols · { SYM: { cik, name } }.
async function companiesOf(symbols) {
  const { data, error } = await supabase.from("companies").select("cik, ticker, name").in("ticker", symbols);
  if (error) { warn("companies", error); return null; }
  return Object.fromEntries((data || []).map(r => [r.ticker, { cik: r.cik, name: r.name }]));
}

/**
 * The reports of the last `days` days that have a checked summary, newest
 * first. Technical-only reports and those with no summary are not cards.
 * `all`: every non-technical report, with or without a summary (the stock
 * panel lists them, and says why one has none).
 * null on a failure, [] when there are none.
 */
export async function fetchSecFilings(symbols, { days = 14, all = false } = {}) {
  const list = [...new Set((symbols || []).filter(Boolean))];
  if (!supabase || !list.length) return [];
  const since = new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
  const { data, error } = await supabase.from("sec_filings")
    .select("accession, symbol, form, filed_date, items, main_item, event_type, doc_url, press_url, summary, summary_status")
    .in("symbol", list).gte("filed_date", since).eq("technical", false)
    .in("summary_status", all ? ["ok", "pending", "failed", "skipped"] : ["ok"])
    .order("filed_date", { ascending: false }).limit(200);
  if (error) { warn("filings", error); return missing(error) ? [] : null; }
  return (data || []).filter(r => all || (Array.isArray(r.summary) && r.summary.length));
}

// "על החברה" (22/10/2026): what the company does, in one or two sentences,
// from the Hebrew translation of the annual report's business section
// (company_business.description_he, a translation, never a paraphrase).
// The section starts with what a reader does not want here, and showing it
// raw read "פריט 1. עסקים כללי Microsoft…" and "תיאור עסקי זה יש לקרוא יחד
// עם הדוחות הכספיים…". So, sentence by sentence, in order:
//   · headings are dropped: a short line with no full stop and nothing a
//     sentence has, or "פריט 1." and the heading words after it
//   · skipped: fixed wording ("יש לקרוא יחד עם", forward-looking statements,
//     what "we" refers to, "in this report"), the legal form and the address
//     ("מאוגדת לפי חוקי", "המשרדים הראשיים"), and · unless the same sentence
//     says what the company is · incorporation history (PEP: "We were
//     incorporated in 1919") and the report's layout (segments, the fiscal
//     year, page references)
//   · the first sentence left with the company as its SUBJECT is the one:
//     its name at the start (the first word of it, or its ticker), "החברה"
//     (Apple: "The Company designs, manufactures…") or "אנו"; or its name
//     with what it is or does right after it (CAT: "With 2025 sales…,
//     Caterpillar Inc. is…"). A name inside a product line is not: Apple's
//     "Home includes Apple TV 4K…" (22/10/2026). Nor a mission: "המשימה של
//     Lucid היא…"
//   · and the sentence after it, in the same paragraph, when it fits
// Checked on the business sections of the app's SEC universe (49 companies)
// and David's 12 holdings. Nothing suitable → "" and the section is hidden.
// Not after "U.S.", "Inc.", "Co." (JPMorgan Chase & Co., Walmart Inc.).
const SENT = /(?<=[.!?])(?<!(?:^|[\s.])[A-Z]\.)(?<!\b(?:Inc|Corp|Co|Ltd|No|St)\.)\s+/;
const HEADING_LEAD = /^(?:(?:פריט|item)\s*\d+[a-z]?\s*[.:]?\s*)?(?:(?:עסקים|העסק|עסק|תיאור העסק|כללי|כללית|סקירה כללית|סקירה תאגידית|סקירת החברה|סקירה|מבוא|רקע על החברה|רקע|אודותינו|אודות החברה|מידע על החברה|משימה|המשימה שלנו|business|general|overview|corporate overview|company overview)\s*[.:·-]?\s+){0,4}/i;
const BOILERPLATE = /יש לקרוא|לקרוא יחד עם|בשילוב עם הדוחות|בצירוף הדוחות|צופות פני עתיד|צופה פני עתיד|השימוש במילים|השימוש במונחים|מתייחס(?:ים|ת)? ל|מתייחסות ל|אלא אם ההקשר|בדוח (?:שנתי )?זה|בטופס|Form 10-K|10-K|20-F/;
// The legal form, the address, the listing: facts about the filer, not what
// the company does (NICE: "a company limited by shares organized under the
// laws of the State of Israel"; WMT: "We maintain our principal offices").
const LEGAL = /חוק החברות|מקום מושבה|שמקום מושבה|מאוגדת לפי|מאוגד לפי|התאגדה לפי|חוקי מדינת|מוגבלת במניות|בערבון מוגבל|משרדי\S* הראשי|ממוקמי\S* ב|נסחר\S* ב(?:בורסת|נאסד|-?Nasdaq|-?NYSE)|תחת הסימול/;
const HISTORY = /התאגד|ארגון מחדש|רה-ארגון|איי קיימן|החלה את פעילות|הוקמה כ|נרשמה כ|נוסד/;
const NOT_ABOUT = /מגזרים|מגזר בר דיווח|שנת הכספים|שנת כספים|עמודים \d|עמ' \d|מדווחים על|מאורגנ\S* ב/;
// What a company is or does: after its name, or anywhere in a sentence that
// also carries history or the report's layout.
const IDENTITY = /^(?:היא|הוא|הינה|הנה|מעצבת|מפתחת|מספקת|מייצרת|פועלת|עוסקת|מובילה|בונה|מציעה|משווקת|מתכננת)$/;
const SUBJECT_WORD = /^(?:אנו|אנחנו|החברה|הקבוצה|we)$/i;
const FIRST_MAX = 400;

export function aboutText(descriptionHe, { name = "", symbol = "", maxChars = FIRST_MAX } = {}) {
  const key = (String(name).replace(/[^A-Za-z0-9& ]+/g, " ").trim().split(/\s+/)[0] || "").toLowerCase();
  const names = [key.length >= 3 ? key : null, String(symbol || "").toLowerCase() || null].filter(Boolean);
  const bare = w => w.replace(/[,.:;)]+$/, "");
  const isName = w => names.includes(w.toLowerCase().replace(/[^a-z0-9&.-]/g, "").replace(/[.]$/, ""));
  const hasIdentity = t => t.split(/\s+/).some(w => IDENTITY.test(bare(w)));
  const speaks = t => {
    if (/^(?:אנו|אנחנו|החברה|הקבוצה)\s/.test(t)) return true;
    const words = t.split(/\s+/);
    const head = words.slice(0, 3);
    const atStart = head.some((w, i) => isName(w)
      && head.slice(0, i).every(b => /^(?:חברת|קבוצת|תאגיד|בנק|The)$/.test(b)));
    if (atStart) return true;
    // Not "המשימה של Lucid היא…": a name after "של" is not the subject.
    return words.some((w, i) => isName(w) && words[i - 1] !== "של"
      && words.slice(i + 1, i + 4).some(n => IDENTITY.test(bare(n))));
  };

  // Lines into paragraphs. The stored text breaks lines inside sentences too
  // (the filing's own layout), so a long line with no full stop at its end
  // runs on into the next.
  const paragraphs = [];
  let cur = "";
  for (const line of String(descriptionHe || "").split(/\n+/).map(l => l.replace(/\s+/g, " ").trim()).filter(Boolean)) {
    const ends = /[.!?:]["”׳']?$/.test(line);
    // A heading: short, no full stop, and nothing a sentence has (a comma, a
    // quote, a bracket) · and not a subject alone on its line ("We\nhave
    // historically designed…", FABC).
    if (!cur && !ends && line.length < 60 && !/[,;“”"()]/.test(line) && !SUBJECT_WORD.test(line)) continue;
    cur = cur ? `${cur} ${line}` : line;
    if (ends) { paragraphs.push(cur); cur = ""; }
  }
  if (cur) paragraphs.push(cur);

  // Paragraph by paragraph: its leading sentences of fixed wording, legal
  // form, history or layout are passed over (PEP: "We were incorporated in
  // 1919. We are a leading global beverage…"); the sentence after them must
  // have the company as its subject, or the paragraph is not the one. A
  // sentence further into a paragraph is never the opening (GOOGL's "We do
  // not intend to become one.").
  const fixed = t => BOILERPLATE.test(t) || LEGAL.test(t);
  const aside = t => fixed(t) || ((HISTORY.test(t) || NOT_ABOUT.test(t)) && !hasIdentity(t));
  for (const raw of paragraphs) {
    const sentences = raw.replace(HEADING_LEAD, "").trim().split(SENT).map(t => t.trim()).filter(Boolean);
    let i = 0;
    while (i < sentences.length && aside(sentences[i])) i++;
    const first = sentences[i];
    if (!first || first.length > maxChars || !speaks(first)) continue;
    const next = sentences[i + 1];
    if (next && !aside(next) && !HISTORY.test(next) && (first + " " + next).length <= maxChars) return `${first} ${next}`;
    return first;
  }
  return "";
}

export async function fetchCompanyAbout(symbols) {
  const list = [...new Set((symbols || []).filter(Boolean))];
  if (!supabase || !list.length) return {};
  const companies = await companiesOf(list);
  if (!companies) return {};
  const symOf = Object.fromEntries(Object.entries(companies).map(([sym, c]) => [c.cik, sym]));
  const ciks = Object.keys(symOf);
  if (!ciks.length) return {};
  const { data, error } = await supabase.from("company_business")
    .select("cik, description_he, source_form, source_url, fiscal_year").in("cik", ciks);
  if (error) { warn("business", error); return {}; }
  const out = {};
  for (const r of data || []) {
    const text = aboutText(r.description_he, { name: companies[symOf[r.cik]]?.name, symbol: symOf[r.cik] });
    if (text && /^https:\/\/www\.sec\.gov\//.test(r.source_url || "")) out[symOf[r.cik]] = { text, url: r.source_url, form: r.source_form || null, year: r.fiscal_year || null };
  }
  return out;
}

// "בעלי עניין שקנו או מכרו" · open-market trades (P, S) of the last 30 days
// in these companies. → [{ symbol, name, title, side, shares, price, date, url }]
// null on a failure.
export async function fetchInsiderTrades(symbols, { days = TRADES_DAYS } = {}) {
  const list = [...new Set((symbols || []).filter(Boolean))];
  if (!supabase || !list.length) return [];
  const companies = await companiesOf(list);
  if (!companies) return null;
  const symOf = Object.fromEntries(Object.entries(companies).map(([sym, c]) => [c.cik, sym]));
  const ciks = Object.keys(symOf);
  if (!ciks.length) return [];
  const since = new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
  const { data, error } = await supabase.from("insider_transactions")
    .select("cik, owner_name, owner_title, transaction_date, transaction_code, shares, price_per_share, source_url")
    .in("cik", ciks).in("transaction_code", ["P", "S"]).gte("transaction_date", since)
    .order("transaction_date", { ascending: false }).limit(300);
  if (error) { warn("insider", error); return null; }
  return openMarketTrades((data || []).map(r => ({ ...r, symbol: symOf[r.cik] })), { days });
}

// The SEC's company names · { SYM: "RIOT PLATFORMS, INC." } · {} on a failure.
export async function fetchCompanyNames(symbols) {
  const list = [...new Set((symbols || []).filter(Boolean))];
  if (!supabase || !list.length) return {};
  const companies = await companiesOf(list);
  return companies ? Object.fromEntries(Object.entries(companies).map(([sym, c]) => [sym, c.name])) : {};
}
